import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import gibletLogo from '@/assets/giblet-logo.png';
import { CheckCircle2 } from 'lucide-react';

export default function DemoCallForDestructionReview() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [reviewerEmail, setReviewerEmail] = useState('');
  const [reviewerMessage, setReviewerMessage] = useState('');

  const actionLabels: Record<string, string> = {
    remove: 'Remove from Platform',
    update: 'Require Content Correction',
    restrict: 'Restrict Access',
    disclaimer: 'Add Disclaimer',
  };

  if (showConfirmation) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
        <div className="max-w-2xl w-full space-y-8 text-center">
          <div className="flex justify-center mb-8">
            <img src={gibletLogo} alt="Giblet Logo" className="h-16 w-auto" />
          </div>
          <CheckCircle2 className="mx-auto h-16 w-16 text-green-500" />
          <h1 className="text-3xl font-bold text-foreground">CfD Submitted Successfully!</h1>
          <p className="text-muted-foreground">
            The KB owner has been notified and has 7 days to respond.
          </p>
          <Button onClick={() => navigate('/demo-call-for-destruction/outcome', { state })}>
            View CfD Outcome Summary
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <div className="max-w-2xl w-full space-y-8">
        <div className="flex justify-center mb-8">
          <img src={gibletLogo} alt="Giblet Logo" className="h-16 w-auto" />
        </div>

        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold text-foreground">Review Call for Destruction</h1>
          <p className="text-lg text-muted-foreground">Confirm all details before submitting</p>
        </div>

        <div className="bg-card rounded-lg p-8 shadow-lg space-y-5">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Issue Type:</span>
              <div className="font-medium text-foreground">{state.issueTypeName || 'N/A'}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Severity:</span>
              <div className="font-medium text-foreground capitalize">{state.severity || 'N/A'}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Target KB:</span>
              <div className="font-medium text-foreground">{state.targetKBName || 'N/A'}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Requested Action:</span>
              <div className="font-medium text-foreground">{actionLabels[state.requestedAction] || 'N/A'}</div>
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <h3 className="font-semibold text-foreground mb-2">CfD Title</h3>
            <p className="text-sm text-foreground">{state.cfdTitle}</p>
          </div>

          <div className="border-t border-border pt-4">
            <h3 className="font-semibold text-foreground mb-2">Evidence</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-line">{state.evidence}</p>
          </div>

          <div className="border-t border-border pt-4">
            <h3 className="font-semibold text-foreground mb-2">Impact Assessment</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-line">{state.impactAssessment}</p>
          </div>

          {state.hasBounty && (
            <div className="border-t border-border pt-4">
              <h3 className="font-semibold text-foreground mb-2">Destruction Bounty</h3>
              <div className="bg-destructive/10 rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span>Total Bounty:</span>
                  <span className="font-bold">${(state.bountyAmount || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Giblet (1%):</span>
                  <span>${((state.bountyAmount || 0) * 0.01).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Community (99%):</span>
                  <span>${((state.bountyAmount || 0) * 0.99).toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}

          <div className="border-t border-border pt-4">
            <h3 className="font-semibold text-foreground mb-2">Response Deadline</h3>
            <p className="text-sm text-foreground">{state.deadline}</p>
          </div>

          <div className="border-t border-border pt-4 space-y-3">
            <h3 className="font-semibold text-foreground">Contact Reviewer (optional)</h3>
            <div className="space-y-2">
              <Label>Reviewer Email</Label>
              <Input value={reviewerEmail} onChange={(e) => setReviewerEmail(e.target.value)} placeholder="email@example.com" />
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea value={reviewerMessage} onChange={(e) => setReviewerMessage(e.target.value)} placeholder="Add context for the reviewer..." rows={3} />
            </div>
          </div>
        </div>

        <div className="flex justify-center gap-4">
          <Button variant="outline" onClick={() => navigate('/demo-call-for-destruction/details')} className="min-w-[120px]">
            Back
          </Button>
          <Button onClick={() => setShowConfirmation(true)} className="min-w-[120px]">
            Submit CfD
          </Button>
        </div>
      </div>
    </div>
  );
}
